import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RECURRENCE_CYCLES,
  RECURRENCE_CYCLE_MAP,
  type RecurrenceCycle,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { useDocumentDefaults } from '../../lib/settings';
import { ErrorState, PageHead } from '../../components/ui';
import { LineEditor, emptyLine, toPayload, type EditableLine } from '../sales/LineEditor';

interface AccountOption {
  id: string;
  name: string;
}

/**
 * إنشاء اشتراك دوري وتعديله.
 *
 * يستعمل محرّر البنود نفسه الذي تستعمله عروض الأسعار والفواتير: القالب
 * فاتورة في كل شيء عدا أنه يُصدر نفسه، فلا سبب لمحرّر ثانٍ يفترق عنه.
 */
export function RecurringEditor() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const defaults = useDocumentDefaults();

  const [accountId, setAccountId] = useState('');
  const [title, setTitle] = useState('');
  const [cycle, setCycle] = useState<RecurrenceCycle>('MONTHLY');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [dueDays, setDueDays] = useState('');
  const [autoIssue, setAutoIssue] = useState(false);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [lines, setLines] = useState<EditableLine[]>([emptyLine()]);
  const [discount, setDiscount] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [loaded, setLoaded] = useState(false);

  const accounts = useQuery({
    queryKey: ['accounts', 'options'],
    queryFn: () => api<{ items: AccountOption[] }>('/crm/accounts?pageSize=200&isActive=true'),
  });

  const existing = useQuery({
    queryKey: ['recurring', id],
    queryFn: () => api<Record<string, any>>(`/finance/recurring/${id}`),
    enabled: editing,
  });

  /* الافتراضيات من إعدادات الشركة، وتُطبَّق على الجديد وحده. */
  useEffect(() => {
    if (editing || loaded || !defaults.ready) return;
    setTaxRate(String(defaults.settings.taxRate));
    setDueDays(String(defaults.settings.invoiceDueDays));
    setTerms(defaults.settings.defaultInvoiceTerms ?? '');
    setLoaded(true);
  }, [defaults, editing, loaded]);

  useEffect(() => {
    const r = existing.data;
    if (!r || loaded) return;
    setAccountId(r.accountId);
    setTitle(r.title);
    setCycle(r.cycle);
    setStartDate(r.startDate);
    setEndDate(r.endDate ?? '');
    setDayOfMonth(String(r.dayOfMonth));
    setDueDays(String(r.dueDays));
    setAutoIssue(r.autoIssue);
    setNotes(r.notes ?? '');
    setTerms(r.terms ?? '');
    setDiscount(String(Number(r.discount) || ''));
    setTaxRate(String(Number(r.taxRate)));
    setLines(
      (r.lines ?? []).map((l: any) => ({
        serviceId: l.serviceId,
        description: l.description,
        quantity: String(Number(l.quantity)),
        unit: l.unit,
        unitPrice: String(Number(l.unitPrice)),
        discount: String(Number(l.discount) || ''),
      })),
    );
    setLoaded(true);
  }, [existing.data, loaded]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title: title.trim(),
        notes: notes.trim() || null,
        terms: terms.trim() || null,
        endDate: endDate || null,
        dayOfMonth: Number(dayOfMonth) || 1,
        discount: Number(discount) || 0,
        taxRate: Number(taxRate) || 0,
        dueDays: Number(dueDays) || 0,
        autoIssue,
        lines: toPayload(lines),
      };

      return editing
        ? api<{ id: string }>(`/finance/recurring/${id}`, { method: 'PATCH', body: payload })
        : api<{ id: string }>('/finance/recurring', {
            method: 'POST',
            body: { ...payload, accountId, cycle, startDate },
          });
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['recurring'] });
      navigate(`/finance/recurring/${saved.id}`);
    },
  });

  if (existing.isError) {
    return <ErrorState error={existing.error} onRetry={() => existing.refetch()} />;
  }

  const valid =
    title.trim().length >= 2 && (editing || accountId) && toPayload(lines).length > 0;

  return (
    <div className="page">
      <PageHead
        title={editing ? 'تعديل اشتراك دوري' : 'اشتراك دوري جديد'}
        subtitle="قالب فاتورة يُصدر نفسه في كل دورة"
        actions={
          <>
            <Link to="/finance/recurring" className="btn">
              رجوع
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => save.mutate()}
              disabled={!valid || save.isPending}
            >
              {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
            </button>
          </>
        }
      />

      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">الاشتراك</h2>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <div className="field span-2">
              <label className="label req" htmlFor="r-title">
                عنوان الاشتراك
              </label>
              <input
                id="r-title"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="أتعاب إدارة حسابات التواصل"
              />
              <span className="tiny mute">
                يظهر على كل فاتورة مع شهرها — مثل "أتعاب إدارة حسابات التواصل — يناير ٢٠٢٦".
              </span>
            </div>

            <div className="field span-2">
              <label className="label req" htmlFor="r-account">
                العميل
              </label>
              <select
                id="r-account"
                className="select"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                disabled={editing}
              >
                <option value="">اختر</option>
                {accounts.data?.items.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {editing && (
                /*
                  العميل لا يتغيّر بعد الإنشاء: الفواتير الصادرة منسوبة إليه،
                  وتغييره يجعل تاريخ اشتراك واحد موزّعًا على عميلين.
                */
                <span className="tiny mute">
                  العميل لا يُغيَّر بعد الإنشاء. أنشئ اشتراكًا جديدًا لعميل آخر.
                </span>
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="r-cycle">
                الدورة
              </label>
              <select
                id="r-cycle"
                className="select"
                value={cycle}
                onChange={(e) => setCycle(e.target.value as RecurrenceCycle)}
                disabled={editing}
              >
                {RECURRENCE_CYCLES.map((c) => (
                  <option key={c} value={c}>
                    {RECURRENCE_CYCLE_MAP[c].label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label req" htmlFor="r-start">
                تاريخ البدء
              </label>
              <input
                id="r-start"
                className="input num"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={editing}
              />
              {editing && <span className="tiny mute">لا يُغيَّر بعد الإنشاء.</span>}
            </div>

            <div className="field">
              <label className="label" htmlFor="r-end">
                تاريخ الانتهاء
              </label>
              <input
                id="r-end"
                className="input num"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <span className="tiny mute">اتركه فارغًا لاشتراك مفتوح.</span>
            </div>

            <div className="field">
              <label className="label" htmlFor="r-day">
                يوم الإصدار من الشهر
              </label>
              <input
                id="r-day"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
              />
              {/*
                اليوم ٢٩ فما فوق يُقصر على آخر الشهر القصير: فاتورة فبراير
                تصدر في الثامن والعشرين لا تنزلق إلى مارس.
              */}
              {Number(dayOfMonth) > 28 && (
                <span className="tiny mute">
                  في الشهور الأقصر تصدر في آخر يوم منها.
                </span>
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="r-due">
                مهلة السداد بالأيام
              </label>
              <input
                id="r-due"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={dueDays}
                onChange={(e) => setDueDays(e.target.value)}
              />
            </div>

            <div className="field span-2">
              <label className="check">
                <input
                  type="checkbox"
                  checked={autoIssue}
                  onChange={(e) => setAutoIssue(e.target.checked)}
                />
                <span>إرسال تلقائي — تُصدر الفاتورة مرسَلة لا مسودة</span>
              </label>
              {/*
                الافتراضي مسودة عن قصد: الفاتورة مستند مالي يذهب إلى عميل،
                وخطأ في بند واحد يخرج إلى ثلاثين عميلًا قبل أن يراه أحد.
              */}
              <span className={`tiny ${autoIssue ? 'neg' : 'mute'}`}>
                {autoIssue
                  ? 'الفواتير ستخرج بلا مراجعة. تأكّد من البنود والأسعار.'
                  : 'الفواتير تُنشأ مسودات ليراجعها أحد قبل إرسالها.'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">بنود الفاتورة</h2>
        </div>
        <div className="card-body">
          <LineEditor
            lines={lines}
            onChange={setLines}
            discount={discount}
            onDiscountChange={setDiscount}
            taxRate={taxRate}
            onTaxRateChange={setTaxRate}
          />
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">الشروط والملاحظات</h2>
        </div>
        <div className="card-body">
          <div className="field">
            <label className="label" htmlFor="r-terms">
              الشروط
            </label>
            <textarea
              id="r-terms"
              className="input"
              rows={4}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
            />
          </div>

          <div className="field mt-4">
            <label className="label" htmlFor="r-notes">
              ملاحظات
            </label>
            <textarea
              id="r-notes"
              className="input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
